import { Suspense } from "react";
import { HashRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

import { hasHostSession, hasHostAccess, hasAdminAccess } from "./connection/host-auth";
import { onExitGame } from "./utilities";
import Editor from "./views/editor";
import Host from "./views/host";
import Main from "./views/main";
import Page404 from "./views/page404";
import Player from "./views/player";
import ThemeEditor from "./views/theme";

import "bootstrap/dist/css/bootstrap.min.css";

onExitGame();

/**
 * Route guard for host-level routes (/host, /editor).
 * Allows access if user has:
 *   - A valid hostGrant with role "host" or "admin", OR
 *   - An existing hostSession (hostToken for an active room)
 * Redirects to "/" otherwise to use the "Host tools" login flow.
 */
const RequireHostAuth = ({ children }) => {
    if (!hasHostAccess() && !hasHostSession()) {
        return <Navigate replace to="/" />;
    }
    return children;
};

/**
 * Route guard for admin-only routes (/theme).
 * Allows access only if user has:
 *   - A valid hostGrant with role "admin"
 * Redirects to "/" otherwise (host-only users cannot access theme editor).
 */
const RequireAdminAuth = ({ children }) => {
    if (!hasAdminAccess()) {
        return <Navigate replace to="/" />;
    }
    return children;
};

const loading = () => (<div>Loading...</div>);

const MainOrNotFound = () => { // NOSONAR
    const params = useParams();
    return /^\d+$/.test(params.roomCode) && "404" !== params.roomCode
        ? <Main location={window.location} roomCode={params.roomCode} />
        : <Navigate replace to="/404" />;
};

function normalizeHash() {
    const hash = (window.location.hash || "").trim().replace(/^#/, "").trim();
    if (hash && !hash.startsWith("/")) {
        window.location.hash = `/${hash}`;
    }
}

function App() {
    normalizeHash();
    return (
        <HashRouter>
            <Suspense fallback={loading()}>
                <Routes>
                    <Route exact path="/" name="Main" element={<Main location={window.location} />} />
                    <Route exact path="/host" name="Host mode" element={
                        <RequireHostAuth><Host /></RequireHostAuth>
                    } />
                    <Route exact path="/editor" name="Question editor" element={
                        <RequireHostAuth><Editor /></RequireHostAuth>
                    } />
                    <Route exact path="/theme" name="Theme editor" element={
                        <RequireAdminAuth><ThemeEditor /></RequireAdminAuth>
                    } />
                    <Route exact path="/player" name="Player mode" element={<Player />} />
                    <Route exact path="/404" name="Page not found" element={<Page404 />} />
                    <Route path="/:roomCode" name="Main (pre-populated)" element={<MainOrNotFound />} />
                    <Route path="*" element={<Navigate replace to="/404" />} />
                </Routes>
            </Suspense>
        </HashRouter>
    );
}

export default App;
